import React, { useState, useEffect } from 'react';
import { format, parse } from 'date-fns';
import { TempleRegistration } from './types';
import { getAssetUrl } from './helpers';
import { User } from '../../lib/api';

interface RegistrationDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRegistration: TempleRegistration | null;
    loading: boolean;
    activeTab: 'new' | 'under_review' | 'setup_pending' | 'existing' | 'rejected';

    // Actions
    onAssignToMe: (registration: TempleRegistration) => void;
    onUpdateVerification: (registrationId: string, type: string, status: string, notes?: string, screenshot?: string) => Promise<void>;
    onReviewClick: (approve: boolean) => void;

    // Assignment
    admins: User[];
    selectedAdminId: string;
    setSelectedAdminId: (id: string) => void;
    onAssignTo: (registrationId: string, adminId: string) => Promise<boolean>;

    // Subscription
    templeId: string | null;
    templeSubscription: any;
    searchingTemple: boolean;
    onSearchTemple: (registration: TempleRegistration) => void;
    onOpenSubscriptionModal: () => void;
    onRemoveSubscription?: () => Promise<void>;
    onUpdateTemple?: (templeId: string, data: any) => Promise<boolean>;
    templeDetails?: any;
    onSave: (registrationId: string, data: any) => Promise<boolean>;
    currentUserId?: string;
}

const EditableSection: React.FC<{
    title: string;
    onSave: (data: any) => Promise<boolean>;
    isEditable: boolean;
    initialData: any;
    renderDisplay: () => React.ReactNode;
    renderEdit: (data: any, onChange: (key: string, val: any) => void) => React.ReactNode;
}> = ({ title, onSave, isEditable, initialData, renderDisplay, renderEdit }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState(initialData ?? {});

    useEffect(() => { setData(initialData ?? {}); }, [initialData]);

    const handleSave = async () => {
        const success = await onSave(data);
        if (success) setIsEditing(false);
    };

    return (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6 font-sans">
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
                {isEditable && !isEditing && (
                    <button onClick={() => setIsEditing(true)} className="text-[var(--color-primary)] hover:opacity-80 text-sm font-medium">
                        Edit
                    </button>
                )}
            </div>
            {isEditing ? (
                <div className="space-y-4">
                    {renderEdit(data, (key, val) => setData((prev: any) => ({ ...prev, [key]: val })))}
                    <div className="flex justify-end gap-2 mt-4">
                        <button onClick={() => { setIsEditing(false); setData(initialData ?? {}); }} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Cancel</button>
                        <button onClick={handleSave} className="px-3 py-1.5 bg-[var(--color-primary)] text-white rounded hover:opacity-90 text-sm">Save</button>
                    </div>
                </div>
            ) : renderDisplay()}
        </div>
    );
};

const TimeRangeInput = ({ value, onChange, label }: { value: string, onChange: (val: string) => void, label: string }) => {
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [isClosed, setIsClosed] = useState(false);

    useEffect(() => {
        if (!value || value.toLowerCase() === 'closed') {
            setIsClosed(true);
            setStart('');
            setEnd('');
            return;
        }
        setIsClosed(false);
        if (value.includes('-')) {
            const [s, e] = value.split('-').map(p => p.trim());
            try {
                let d1 = parse(s, 'h:mm a', new Date());
                if (isNaN(d1.getTime())) d1 = parse(s, 'HH:mm', new Date());

                let d2 = parse(e, 'h:mm a', new Date());
                if (isNaN(d2.getTime())) d2 = parse(e, 'HH:mm', new Date());

                if (!isNaN(d1.getTime())) setStart(format(d1, 'HH:mm'));
                if (!isNaN(d2.getTime())) setEnd(format(d2, 'HH:mm'));
            } catch (err) {
                // Fallback: try parsing without AM/PM or other formats if needed
                // But for now clear if invalid to avoid --:-- confusion
            }
        }
    }, [value]);

    const update = (newStart: string, newEnd: string, newClosed: boolean) => {
        setStart(newStart);
        setEnd(newEnd);
        setIsClosed(newClosed);

        if (newClosed) {
            onChange('Closed');
        } else if (newStart && newEnd) {
            const fmt = (t: string) => {
                try { return format(parse(t, 'HH:mm', new Date()), 'h:mm a'); } catch { return ''; }
            };
            onChange(`${fmt(newStart)} - ${fmt(newEnd)}`);
        }
    };

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 border-b border-gray-100 last:border-0 pb-2 mb-2 sm:pb-0 sm:mb-0 sm:border-0 bg-white p-2 rounded hover:bg-gray-50">
            <label className="w-24 text-sm font-medium text-gray-700 capitalize">{label}</label>
            <div className="flex items-center gap-2 flex-1">
                <label className="inline-flex items-center text-xs mr-2 cursor-pointer select-none">
                    <input type="checkbox" className="rounded text-[var(--color-primary)] focus:ring-[var(--color-primary)] mr-1"
                        checked={isClosed} onChange={e => update(start, end, e.target.checked)} />
                    <span className="text-gray-500">Closed</span>
                </label>
                {!isClosed && (
                    <>
                        <input type="time" className="block w-full sm:w-32 rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] text-sm"
                            value={start} onChange={e => update(e.target.value, end, false)} />
                        <span className="text-gray-400">-</span>
                        <input type="time" className="block w-full sm:w-32 rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] text-sm"
                            value={end} onChange={e => update(start, e.target.value, false)} />
                    </>
                )}
            </div>
        </div>
    );
};

const VerificationItem: React.FC<{
    item: any;
    label: string;
    field: string;
    registrationId: string;
    status: string; // overall registration status
    onUpdate: (id: string, type: string, status: string, notes?: string, screenshot?: string) => Promise<void>;
    readOnly?: boolean;
}> = ({ item, label, field, registrationId, status, onUpdate, readOnly }) => {
    const [note, setNote] = useState(item?.notes || '');
    const [uploading, setUploading] = useState(false);
    const [previewScreenshot, setPreviewScreenshot] = useState(item?.screenshot);

    // Sync note and screenshot if item changes externally
    useEffect(() => {
        setNote(item?.notes || '');
        setPreviewScreenshot(item?.screenshot);
    }, [item?.notes, item?.screenshot]);

    const handleStatusChange = async (newStatus: string) => {
        await onUpdate(registrationId, field, newStatus, note, previewScreenshot || item?.screenshot);
    };

    const handleNoteBlur = async () => {
        if (note !== item?.notes) {
            await onUpdate(registrationId, field, item?.status || 'pending', note, previewScreenshot || item?.screenshot);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setUploading(true);
            const { adminApiClient } = await import('../../lib/api');
            const toast = (await import('react-hot-toast')).toast;

            const uploadToast = toast.loading('Uploading proof...');
            const response = await adminApiClient.uploadFile('documents', file);

            if (response.success && response.data) {
                // Ensure we get a valid URL string
                const fileUrl = typeof response.data === 'string'
                    ? response.data
                    : (response.data.url || (response.data as any).fileUrl || (response.data as any).location || (response.data as any).path || (response.data as any).fullPath);

                if (fileUrl) {
                    setPreviewScreenshot(fileUrl); // Update local state immediately
                    await onUpdate(registrationId, field, item?.status || 'pending', note, fileUrl);
                    toast.success('Proof uploaded', { id: uploadToast });
                } else {
                    toast.error('Upload successful but no URL returned', { id: uploadToast });
                }
            } else {
                toast.error('Upload failed', { id: uploadToast });
            }
        } catch (err) {
            console.error(err);
            const toast = (await import('react-hot-toast')).toast;
            toast.error('Upload error');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                <span className="font-semibold text-gray-800 text-sm">{label}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase ${item?.status === 'verified' ? 'bg-green-100 text-green-700' :
                    item?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                    }`}>
                    {item?.status || 'PENDING'}
                </span>
            </div>

            {/* Body */}
            <div className="p-4 flex-1 flex flex-col gap-4">
                {/* Proof Section */}
                <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Proof Document</p>

                    <div className="flex flex-col gap-2">
                        {/* Original Document Link */}
                        {item.originalDocument && (
                            <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-100 mb-1">
                                <span className="text-lg">📎</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] font-bold text-purple-900 uppercase tracking-wider mb-0.5">
                                        {item.originalDocumentLabel || 'Original Upload'}
                                    </p>
                                    <a
                                        href={getAssetUrl(item.originalDocument)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-purple-700 font-medium hover:underline truncate block"
                                    >
                                        View Attached Document
                                    </a>
                                </div>
                            </div>
                        )}

                        {(previewScreenshot || item?.screenshot) ? (
                            <div className="flex items-center gap-2 p-2 bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] rounded-lg border border-[color-mix(in_srgb,var(--color-secondary)_20%,white)]">
                                <span className="text-lg">📄</span>
                                <a
                                    href={getAssetUrl(previewScreenshot || item.screenshot)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-[var(--color-secondary)] font-medium hover:underline truncate flex-1"
                                >
                                    View Uploaded Proof
                                </a>
                                <span className="text-[10px] text-[color-mix(in_srgb,var(--color-secondary)_45%,white)]">Changed? Upload new</span>
                            </div>
                        ) : (
                            <div className="text-xs text-gray-400 italic p-2 bg-gray-50 rounded border border-dashed border-gray-200 text-center">
                                No proof uploaded yet
                            </div>
                        )}
                    </div>
                </div>

                {/* Edit Section */}
                {status !== 'rejected' && !readOnly && (
                    <div className="space-y-3 pt-3 border-t border-gray-100 mt-auto">
                        {/* Status Select */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Update Status</label>
                            <select
                                className="block w-full text-xs border border-gray-300 rounded-lg focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] py-1.5"
                                value={item?.status || 'pending'}
                                onChange={(e) => handleStatusChange(e.target.value)}
                            >
                                <option value="pending">⏳ Pending</option>
                                <option value="verified">✅ Verified</option>
                                <option value="rejected">❌ Rejected</option>
                            </select>
                        </div>

                        {/* File Upload */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                {(previewScreenshot || item?.screenshot) ? 'Replace Proof' : 'Upload Proof / Screenshot'}
                            </label>
                            <div className="flex items-center gap-2">
                                <label className="flex-1 cursor-pointer">
                                    <div className={`flex items-center justify-center px-4 py-2 border border-dashed rounded-lg text-xs font-medium transition-colors ${uploading ? 'bg-gray-100 border-gray-300 text-gray-400' : 'border-[color-mix(in_srgb,var(--color-primary)_35%,white)] bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[color-mix(in_srgb,var(--color-primary)_80%,black)] hover:bg-[color-mix(in_srgb,var(--color-primary)_18%,white)]'
                                        }`}>
                                        {uploading ? (
                                            <span className="flex items-center gap-1">
                                                <span className="w-3 h-3 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin"></span>
                                                Uploading...
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1">
                                                <span>📤</span>
                                                {(previewScreenshot || item?.screenshot) ? 'Change File' : 'Choose File...'}
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*,application/pdf"
                                        onChange={handleFileUpload}
                                        disabled={uploading}
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                                className="block w-full text-xs border border-gray-300 rounded-lg focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] min-h-[60px]"
                                placeholder="Add internal notes..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                onBlur={handleNoteBlur}
                            />
                        </div>
                    </div>
                )}

                {/* Read Only Notes */}
                {((status !== 'pending' && status !== 'approved' && item?.notes) || (readOnly && item?.notes)) && (
                    <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 italic border border-yellow-100">
                        "{item.notes}"
                    </div>
                )}
            </div>
        </div>
    );
};
export const RegistrationDetailsModal: React.FC<RegistrationDetailsModalProps> = ({
    isOpen,
    onClose,
    selectedRegistration,
    loading,
    activeTab,
    onAssignToMe,
    onUpdateVerification,
    onReviewClick,
    admins,
    selectedAdminId,
    setSelectedAdminId,
    onAssignTo,
    templeId,
    templeSubscription,
    searchingTemple,
    onSearchTemple,
    onOpenSubscriptionModal,
    onRemoveSubscription,
    onUpdateTemple,
    templeDetails,
    onSave,
    currentUserId
}) => {
    // Local State for Form
    const [status, setStatus] = useState<string>('pending');
    const [assignedTo, setAssignedTo] = useState<string>('');
    const [rejectionReason, setRejectionReason] = useState<string>('');
    const [grantCustomDomain, setGrantCustomDomain] = useState<boolean>(true);
    const [canTempleAdminManageDomain, setCanTempleAdminManageDomain] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize state when modal opens
    useEffect(() => {
        if (selectedRegistration) {
            setStatus(selectedRegistration.status);
            setAssignedTo(selectedRegistration.assignedTo?._id || '');
            setRejectionReason('');
            setGrantCustomDomain(true);
            setCanTempleAdminManageDomain(false);
        }
    }, [selectedRegistration, isOpen]);

    const handleGlobalSave = async () => {
        if (!selectedRegistration) return;
        setIsSaving(true);
        await onSave(selectedRegistration._id, {
            status,
            assignedTo: assignedTo || null,
            rejectionReason,
            grantCustomDomain,
            canTempleAdminManageDomain
        });
        setIsSaving(false);
    };

    if (!isOpen || !selectedRegistration) return null;

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-yellow-100 text-yellow-800';
        }
    };



    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[90vh] overflow-y-auto">
                        <div className="sm:flex sm:items-start justify-between mb-6">
                            <div className="flex items-center gap-4">
                                {selectedRegistration.temple.logo && (
                                    <img
                                        src={getAssetUrl(selectedRegistration.temple.logo)}
                                        alt="Logo"
                                        className="w-16 h-16 rounded-full object-cover border border-gray-200"
                                    />
                                )}
                                <div>
                                    <h3 className="text-2xl leading-6 font-bold text-gray-900" id="modal-title">
                                        {selectedRegistration.temple.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Received on {format(new Date(selectedRegistration.createdAt), 'PPP')}
                                    </p>
                                </div>
                            </div>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 text-2xl">×</button>
                        </div>

                        <div className="mt-4">
                            {/* Management Section (Status & Assignment) */}
                            <div className="bg-white p-4 rounded-lg mb-6 border border-gray-200 shadow-sm">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <span>⚙️</span> Management
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Status Section */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Application Status</label>
                                        <select
                                            className={`block w-full border border-gray-300 rounded-lg p-2.5 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] ${status === 'approved' ? 'bg-green-50 text-green-700 font-medium' :
                                                status === 'rejected' ? 'bg-red-50 text-red-700 font-medium' :
                                                    'bg-yellow-50 text-yellow-700 font-medium'
                                                }`}
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            disabled={loading || isSaving || activeTab === 'new'}
                                        >
                                            <option value="pending">⏳ Pending Review</option>
                                            <option value="approved">✅ Approved</option>
                                            <option value="rejected">❌ Rejected</option>
                                        </select>

                                        {status === 'approved' && selectedRegistration.status !== 'approved' && selectedRegistration.temple?.customDomain && (
                                            <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200 text-xs space-y-2 animate-fadeIn">
                                                <p className="font-semibold text-orange-950">
                                                    Custom Domain Requested: <span className="font-mono text-orange-700">{selectedRegistration.temple.customDomain}</span>
                                                </p>
                                                <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={grantCustomDomain}
                                                        onChange={(e) => setGrantCustomDomain(e.target.checked)}
                                                        className="rounded text-orange-600 focus:ring-orange-500"
                                                    />
                                                    <span>Enable Custom Domain Feature on Approval</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={canTempleAdminManageDomain}
                                                        onChange={(e) => setCanTempleAdminManageDomain(e.target.checked)}
                                                        className="rounded text-orange-600 focus:ring-orange-500"
                                                    />
                                                    <span>Allow Temple Admin to self-manage custom domain</span>
                                                </label>
                                            </div>
                                        )}

                                        {status === 'rejected' && (
                                            <div className="mt-3 animate-fadeIn">
                                                <label className="block text-xs font-medium text-red-700 mb-1">Rejection Reason *</label>
                                                <textarea
                                                    className="block w-full border border-red-300 rounded-lg p-2 text-sm focus:ring-red-500 focus:border-red-500 bg-red-50 min-h-[80px]"
                                                    placeholder="Please provide a reason for rejection..."
                                                    value={rejectionReason}
                                                    onChange={(e) => setRejectionReason(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Assignment Section - Hide for existing temples or when setup is pending for an approved registration */}
                                    {activeTab === 'existing' || (selectedRegistration.status === 'approved' && selectedRegistration.setupStatus === 'pending') ? (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                                            <div className="block w-full border border-gray-200 bg-gray-50 rounded-lg p-2.5 text-gray-900">
                                                {selectedRegistration.assignedTo?.name ? (
                                                    <span className="flex items-center gap-2">
                                                        <span>👤</span> {selectedRegistration.assignedTo.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500 italic">Unassigned</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                                            <select
                                                className="block w-full border border-gray-300 rounded-lg p-2.5 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                                                value={assignedTo}
                                                onChange={(e) => setAssignedTo(e.target.value)}
                                                disabled={loading || isSaving}
                                            >
                                                <option value="">-- Unassigned --</option>
                                                {currentUserId && (
                                                    <option value={currentUserId} className="font-bold">
                                                        👤 Assign to Myself
                                                    </option>
                                                )}
                                                <optgroup label="Admins">
                                                    {admins.map(admin => (
                                                        <option key={admin._id || admin.id} value={admin._id || admin.id}>
                                                            {admin.name} ({admin.email})
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Verification Status - 3 Pillars */}
                            <div className="mb-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-3">Verification Checks</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <VerificationItem
                                        item={{
                                            ...selectedRegistration.verificationStatus?.certificate501c3,
                                            originalDocument: selectedRegistration.temple.certificate501c3,
                                            originalDocumentLabel: "Uploaded by Temple Admin"
                                        }}
                                        label="501(c)(3) Certificate"
                                        field="certificate501c3"
                                        registrationId={selectedRegistration._id}
                                        status={selectedRegistration.status}
                                        onUpdate={onUpdateVerification}
                                        readOnly={activeTab === 'new'}
                                    />
                                    <VerificationItem
                                        item={selectedRegistration.verificationStatus?.stateVerification}
                                        label="State Registration"
                                        field="stateVerification"
                                        registrationId={selectedRegistration._id}
                                        status={selectedRegistration.status}
                                        onUpdate={onUpdateVerification}
                                        readOnly={activeTab === 'new'}
                                    />
                                    <VerificationItem
                                        item={selectedRegistration.verificationStatus?.irsPropublica}
                                        label="IRS / ProPublica"
                                        field="irsPropublica"
                                        registrationId={selectedRegistration._id}
                                        status={selectedRegistration.status}
                                        onUpdate={onUpdateVerification}
                                        readOnly={activeTab === 'new'}
                                    />
                                </div>
                            </div>

                            {/* Appeals Section */}
                            {selectedRegistration.appeals && selectedRegistration.appeals.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
                                    <h4 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
                                        <span>📢</span>
                                        Appeals History
                                    </h4>
                                    <div className="space-y-4">
                                        {selectedRegistration.appeals.map((appeal: any, idx: number) => (
                                            <div key={idx} className="bg-white p-4 rounded-lg border border-red-100 shadow-sm">
                                                <div className="flex justify-between items-start mb-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${appeal.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        appeal.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {appeal.status.toUpperCase()}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {format(new Date(appeal.submittedAt), 'MMM dd, yyyy HH:mm')}
                                                    </span>
                                                </div>
                                                <p className="text-gray-800 text-sm mb-3">"{appeal.message}"</p>

                                                {appeal.response && (
                                                    <div className="bg-gray-50 p-3 rounded border-l-4 border-[var(--color-secondary)]">
                                                        <p className="text-xs font-semibold text-[color-mix(in_srgb,var(--color-secondary)_85%,black)] mb-1">Admin Response:</p>
                                                        <p className="text-gray-700 text-sm">{appeal.response}</p>
                                                        {appeal.respondedAt && (
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                - {format(new Date(appeal.respondedAt), 'MMM dd, yyyy')}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Main Information Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-6">
                                {/* Admin/Contact Information */}
                                <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <span>👤</span>
                                        Admin Information
                                    </h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex items-start">
                                            <span className="font-semibold text-gray-700 w-24">Name:</span>
                                            <span className="text-gray-900">{selectedRegistration.user.name}</span>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="font-semibold text-gray-700 w-24">Email:</span>
                                            <a href={`mailto: ${selectedRegistration.user.email} `} className="text-[var(--color-secondary)] hover:opacity-80">
                                                {selectedRegistration.user.email}
                                            </a>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="font-semibold text-gray-700 w-24">Phone:</span>
                                            <a href={`tel: ${selectedRegistration.user.phone} `} className="text-[var(--color-secondary)] hover:opacity-80">
                                                {selectedRegistration.user.phone}
                                            </a>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="font-semibold text-gray-700 w-24">Address:</span>
                                            <span className="text-gray-900">
                                                {selectedRegistration.user.address.street}, {selectedRegistration.user.address.city}, {selectedRegistration.user.address.state} {selectedRegistration.user.address.zipCode}, {selectedRegistration.user.address.country}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Stripe Payment Gateway Information */}
                                <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <span>💳</span>
                                        Payment Gateway (Stripe)
                                    </h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-700">Publishable Key:</span>
                                            <span className="text-gray-900 font-mono text-xs mt-1 break-all bg-gray-50 p-2 rounded border border-gray-100">
                                                {selectedRegistration.temple.stripePublishableKey || <span className="text-gray-400 italic">Not provided</span>}
                                            </span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-700">Secret Key:</span>
                                            <span className="text-gray-900 font-mono text-xs mt-1 break-all bg-gray-50 p-2 rounded border border-gray-100">
                                                {selectedRegistration.temple.stripeSecretKey ? '••••••••••••••••••••••••••••••••••••' : <span className="text-gray-400 italic">Not provided</span>}
                                            </span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-700">Webhook Secret:</span>
                                            <span className="text-gray-900 font-mono text-xs mt-1 break-all bg-gray-50 p-2 rounded border border-gray-100">
                                                {selectedRegistration.temple.stripeWebhookSecret ? '••••••••••••••••••••••••••••••••••••' : <span className="text-gray-400 italic">Not provided</span>}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Temple Information */}
                                <EditableSection
                                    title="Temple Information"
                                    isEditable={!!templeId && !!onUpdateTemple}
                                    initialData={templeDetails || selectedRegistration.temple}
                                    onSave={async (data) => {
                                        if (templeId && onUpdateTemple) {
                                            return await onUpdateTemple(templeId, {
                                                name: data.name,
                                                deity: data.deity,
                                                description: data.description,
                                                establishedYear: data.establishedYear
                                            });
                                        }
                                        return false;
                                    }}
                                    renderDisplay={() => {
                                        const t = templeDetails || selectedRegistration.temple;
                                        return (
                                            <div className="space-y-3 text-sm">
                                                <div className="flex items-start">
                                                    <span className="font-semibold text-gray-700 w-24">Name:</span>
                                                    <span className="text-gray-900">{t.name}</span>
                                                </div>
                                                <div className="flex items-start">
                                                    <span className="font-semibold text-gray-700 w-24">Deity:</span>
                                                    <span className="text-gray-900">{t.deity}</span>
                                                </div>
                                                {t.establishedYear && (
                                                    <div className="flex items-start">
                                                        <span className="font-semibold text-gray-700 w-24">Established:</span>
                                                        <span className="text-gray-900">{t.establishedYear}</span>
                                                    </div>
                                                )}
                                                <div className="flex items-start">
                                                    <span className="font-semibold text-gray-700 w-24">Description:</span>
                                                    <span className="text-gray-900">{t.description}</span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                    renderEdit={(data, onChange) => (
                                        <div className="space-y-3">
                                            <div><label className="block text-xs font-medium text-gray-700">Name</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.name || ''} onChange={e => onChange('name', e.target.value)} /></div>
                                            <div><label className="block text-xs font-medium text-gray-700">Deity</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.deity || ''} onChange={e => onChange('deity', e.target.value)} /></div>
                                            <div><label className="block text-xs font-medium text-gray-700">Established Year</label><input onWheel={(e) => (e.target as HTMLElement).blur()} type="number" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.establishedYear || ''} onChange={e => onChange('establishedYear', parseInt(e.target.value))} /></div>
                                            <div><label className="block text-xs font-medium text-gray-700">Description</label><textarea rows={3} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.description || ''} onChange={e => onChange('description', e.target.value)} /></div>
                                        </div>
                                    )}
                                />

                                {/* Temple Address */}
                                <EditableSection
                                    title="Temple Address"
                                    isEditable={!!templeId && !!onUpdateTemple}
                                    initialData={templeDetails?.address || selectedRegistration.temple.address}
                                    onSave={async (data) => {
                                        if (templeId && onUpdateTemple) {
                                            return await onUpdateTemple(templeId, { address: data });
                                        }
                                        return false;
                                    }}
                                    renderDisplay={() => {
                                        const addr = templeDetails?.address || selectedRegistration.temple.address;
                                        return (
                                            <div className="space-y-2 text-sm text-gray-900">
                                                <p>{addr.street}</p>
                                                <p>{addr.city}, {addr.state} {addr.zipCode}</p>
                                                <p>{addr.country}</p>
                                            </div>
                                        );
                                    }}
                                    renderEdit={(data, onChange) => (
                                        <div className="space-y-3">
                                            <div><label className="block text-xs font-medium text-gray-700">Street</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.street || ''} onChange={e => onChange('street', e.target.value)} /></div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div><label className="block text-xs font-medium text-gray-700">City</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.city || ''} onChange={e => onChange('city', e.target.value)} /></div>
                                                <div><label className="block text-xs font-medium text-gray-700">State</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.state || ''} onChange={e => onChange('state', e.target.value)} /></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div><label className="block text-xs font-medium text-gray-700">Zip Code</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.zipCode || ''} onChange={e => onChange('zipCode', e.target.value)} /></div>
                                                <div><label className="block text-xs font-medium text-gray-700">Country</label><input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)] sm:text-sm" value={data.country || ''} onChange={e => onChange('country', e.target.value)} /></div>
                                            </div>
                                        </div>
                                    )}
                                />

                                {/* Temple Contact */}
                                <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <span>📞</span>
                                        Temple Contact
                                    </h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex items-start">
                                            <span className="font-semibold text-gray-700 w-24">Phone:</span>
                                            <a href={`tel: ${selectedRegistration.temple.contact.phone} `} className="text-[var(--color-secondary)] hover:opacity-80">
                                                {selectedRegistration.temple.contact.phone}
                                            </a>
                                        </div>
                                        <div className="flex items-start">
                                            <span className="font-semibold text-gray-700 w-24">Email:</span>
                                            <a href={`mailto: ${selectedRegistration.temple.contact.email} `} className="text-[var(--color-secondary)] hover:opacity-80">
                                                {selectedRegistration.temple.contact.email}
                                            </a>
                                        </div>
                                        {selectedRegistration.temple.contact.website && (
                                            <div className="flex items-start">
                                                <span className="font-semibold text-gray-700 w-24">Website:</span>
                                                <a href={selectedRegistration.temple.contact.website} target="_blank" rel="noopener noreferrer" className="text-[var(--color-secondary)] hover:opacity-80">
                                                    {selectedRegistration.temple.contact.website}
                                                </a>
                                            </div>
                                        )}
                                        {selectedRegistration.temple.customDomain && (
                                            <div className="flex items-start">
                                                <span className="font-semibold text-gray-700 w-24">Domain:</span>
                                                <span className="font-mono text-orange-600 font-medium">
                                                    {selectedRegistration.temple.customDomain}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Timings */}
                            <EditableSection
                                title="Temple Timings"
                                isEditable={!!templeId && !!onUpdateTemple}
                                initialData={templeDetails?.timings || selectedRegistration.temple.timings}
                                onSave={async (data) => {
                                    if (templeId && onUpdateTemple) {
                                        return await onUpdateTemple(templeId, { timings: data });
                                    }
                                    return false;
                                }}
                                renderDisplay={() => {
                                    const timings = templeDetails?.timings || selectedRegistration.temple.timings;
                                    return (
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                            {Object.entries(timings || {}).map(([day, time]) => (
                                                <div key={day} className="bg-gray-50 rounded-lg p-3 text-center">
                                                    <div className="text-xs font-semibold text-gray-600 uppercase mb-1">{day}</div>
                                                    <div className="text-sm font-medium text-gray-900">{time as string}</div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                }}
                                renderEdit={(data, onChange) => (
                                    <div className="grid grid-cols-1 gap-2">
                                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                            <TimeRangeInput
                                                key={day}
                                                label={day}
                                                value={data[day] || ''}
                                                onChange={(val) => onChange(day, val)}
                                            />
                                        ))}
                                    </div>
                                )}
                            />

                            {/* Facilities */}
                            {selectedRegistration.temple.facilities.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 shadow-sm">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <span>✨</span>
                                        Facilities
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedRegistration.temple.facilities.map((facility, index) => (
                                            <span key={index} className="bg-[color-mix(in_srgb,var(--color-secondary)_12%,white)] text-[color-mix(in_srgb,var(--color-secondary)_85%,black)] px-3 py-1.5 rounded-full text-sm font-medium">
                                                {facility}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Subscription Management Section - Only for approved registrations */}
                            {selectedRegistration.status === 'approved' && (
                                <div className="bg-[linear-gradient(90deg,color-mix(in_srgb,var(--color-secondary)_10%,white),color-mix(in_srgb,var(--color-tertiary)_10%,white))] border-2 border-purple-200 rounded-lg p-6 mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <span>💳</span>
                                            Subscription Management
                                        </h4>
                                        <div className="text-right">
                                            {searchingTemple ? (
                                                <div className="text-xs text-[var(--color-secondary)] mb-2">
                                                    🔍 Searching for temple...
                                                </div>
                                            ) : !templeId ? (
                                                <div className="flex flex-col gap-2 items-end">
                                                    <div className="text-xs text-red-600 font-semibold">
                                                        ⚠️ Temple not found
                                                    </div>
                                                    <button
                                                        onClick={() => onSearchTemple(selectedRegistration)}
                                                        className="btn-secondary text-xs px-3 py-1"
                                                        disabled={searchingTemple}
                                                    >
                                                        🔍 Search Temple
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-xs text-green-600 mb-2">
                                                    ✓ Temple ID: {templeId.substring(0, 8)}...
                                                </div>
                                            )}
                                            <div className="flex gap-2 mt-2">
                                                <button
                                                    onClick={onOpenSubscriptionModal}
                                                    className={`btn-primary text-sm px-4 py-2 ${!templeId || searchingTemple ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    disabled={!templeId || searchingTemple}
                                                    title={!templeId ? 'Temple ID not found. Click "Search Temple" button first.' : 'Assign or change subscription plan'}
                                                >
                                                    {templeSubscription && templeSubscription.plan ? 'Change Plan' : 'Assign Plan'}
                                                </button>
                                                {templeSubscription && templeSubscription.plan && onRemoveSubscription && (
                                                    <button
                                                        onClick={onRemoveSubscription}
                                                        disabled={!templeId || searchingTemple}
                                                        className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Remove subscription from this temple"
                                                    >
                                                        Remove Subscription
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {templeId ? (
                                        <>
                                            {templeSubscription && templeSubscription.plan ? (
                                                <div className="bg-white rounded-lg p-4 border border-purple-200">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Current Plan:</span>
                                                            <span className="text-gray-900 ml-2">{templeSubscription.plan.name}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Status:</span>
                                                            <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${templeSubscription.subscription?.subscriptionStatus === 'active'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                {templeSubscription.subscription?.subscriptionStatus?.toUpperCase() || 'NONE'}
                                                            </span>
                                                        </div>
                                                        {templeSubscription.subscription?.subscriptionEndDate && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Expires:</span>
                                                                <span className="text-gray-900 ml-2">
                                                                    {format(new Date(templeSubscription.subscription.subscriptionEndDate), 'MMM dd, yyyy')}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Price:</span>
                                                            <span className="text-gray-900 ml-2">
                                                                ${templeSubscription.plan.price} {templeSubscription.plan.currency} / {templeSubscription.plan.billingCycle}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                                    <p className="text-sm text-yellow-800">
                                                        <strong>No subscription assigned.</strong> After verification, the temple must purchase a subscription. You can assign a plan here (admin-granted) or the temple admin can purchase via the temple dashboard.
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <p className="text-sm text-red-800">
                                                <strong>Temple not found.</strong> Unable to manage subscription. The temple may have been deleted or the registration data is incomplete.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Status Information */}
                            {selectedRegistration.status !== 'pending' && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Review Information</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-700">Status: </span>
                                            <span className={`status-badge ${getStatusColor(selectedRegistration.status)} px-3 py-1`}>
                                                {selectedRegistration.status.toUpperCase()}
                                            </span>
                                        </div>
                                        {selectedRegistration.reviewedBy && (
                                            <p><strong className="text-gray-700">Reviewed by:</strong> <span className="text-gray-900">{selectedRegistration.reviewedBy.name}</span></p>
                                        )}
                                        {selectedRegistration.reviewedAt && (
                                            <p><strong className="text-gray-700">Reviewed on:</strong> <span className="text-gray-900">{format(new Date(selectedRegistration.reviewedAt), 'MMM dd, yyyy HH:mm')}</span></p>
                                        )}
                                        {selectedRegistration.rejectionReason && (
                                            <div className="mt-3 pt-3 border-t border-gray-300">
                                                <p><strong className="text-gray-700">Rejection Reason:</strong></p>
                                                <p className="text-gray-900 mt-1">{selectedRegistration.rejectionReason}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-100">
                            <button
                                type="button"
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[var(--color-primary)] text-base font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-primary)] sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                onClick={handleGlobalSave}
                                disabled={loading || isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                                onClick={onClose}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-primary)] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
